// src/components/ErrorBoundary.tsx
import { Component, ReactNode, ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { captureError } from '../lib/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'page' | 'component' | 'route';
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Report to Sentry in production
    captureError(error, {
      componentStack: errorInfo.componentStack,
      level: this.props.level ?? 'component',
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private getErrorLevelConfig() {
    const { level = 'component' } = this.props;

    const configs = {
      page: {
        title: 'Page Error',
        description: 'This page encountered an unexpected error.',
        showDetails: true,
        showHomeButton: true,
      },
      component: {
        title: 'Component Error',
        description: 'A component on this page encountered an error.',
        showDetails: false,
        showHomeButton: false,
      },
      route: {
        title: 'Navigation Error',
        description: 'There was an error loading this page.',
        showDetails: true,
        showHomeButton: true,
      },
    };

    return configs[level];
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const config = this.getErrorLevelConfig();
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-orange-50 dark:bg-gray-900">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 text-center">
            <div className="mb-6">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="font-display text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {config.title}
              </h1>
              <p className="text-gray-600 dark:text-gray-300">{config.description}</p>
            </div>

            <div className="space-y-3 mb-6">
              <button
                onClick={this.handleRetry}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Try Again</span>
              </button>

              {config.showHomeButton && (
                <button
                  onClick={this.handleGoHome}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  <Home className="w-4 h-4" />
                  <span>Go Home</span>
                </button>
              )}
            </div>

            {(config.showDetails || isDevelopment) && this.state.error && (
              <details className="text-left">
                <summary className="flex items-center justify-center space-x-2 cursor-pointer text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                  <Bug className="w-4 h-4" />
                  <span>Technical Details</span>
                </summary>
                <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg text-xs">
                  <div className="mb-2">
                    <strong className="text-red-600 dark:text-red-400">Error:</strong>
                    <pre className="mt-1 whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                      {this.state.error.message}
                    </pre>
                  </div>
                  {isDevelopment && this.state.errorInfo && (
                    <div>
                      <strong className="text-red-600 dark:text-red-400">Stack:</strong>
                      <pre className="mt-1 whitespace-pre-wrap text-gray-700 dark:text-gray-300 text-xs">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
              If this problem persists, please contact support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
